import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ScreenState } from '@enums/index';
import { getAllPortfolioDashboardAsync } from './thunks';
import { AdviserSummary, ClientAccount, ClientPortfolioInfo, DashboardDetails, MultiPortfolioInfo } from 'clientportalshared';
import { DateTime } from 'luxon';
import { getEarliestDate } from '@src/app/utils/GenericUtils';

export interface PortfoliosStore {
  portfolioInfoState: ScreenState;
  portfolioInfo: MultiPortfolioInfo;
  portfoliosSelected: ClientPortfolioInfo[];
  portfoliosIdsSelected: number[];
  portfoliosKeysSelected: string[];
  portfoliosCombined: boolean;
  earliestInception: string;
  clientAccounts: ClientAccount[];
  adviser?: AdviserSummary;
  logoUrl?: string;
}

const initialState: PortfoliosStore = {
  portfolioInfoState: ScreenState.Loading,
  portfolioInfo: {} as MultiPortfolioInfo,
  portfoliosKeysSelected: [],
  portfoliosSelected: [],
  portfoliosIdsSelected: [],
  portfoliosCombined: false,
  earliestInception: DateTime.now().toISODate(),
  adviser: undefined,
  logoUrl: undefined,
  clientAccounts: [],
};

export const PortfoliosSlice = createSlice({
  name: 'portfolios',
  initialState,
  reducers: {
    selectOnePortfolioByKey: (state, action: PayloadAction<string>) => {
      state.portfoliosSelected = state.portfoliosSelected.filter((portfolio) => portfolio.portfolioKey === action.payload);
      state.portfoliosIdsSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioId || 0);
      state.portfoliosKeysSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioKey || '');
    },
    removeSelectedPortfolio: (state, action: PayloadAction<number>) => {
      state.portfoliosSelected = state.portfoliosSelected.filter((portfolio) => portfolio.portfolioId !== action.payload);
      state.portfoliosIdsSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioId || 0);
      state.portfoliosKeysSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioKey || '');
    },
    addSelectedPortfolio: (state, action: PayloadAction<ClientPortfolioInfo>) => {
      state.portfoliosSelected.push(action.payload);
      state.portfoliosIdsSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioId || 0);
      state.portfoliosKeysSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioKey || '');
    },
    setCombining: (state, action: PayloadAction<boolean>) => {
      state.portfoliosCombined = action.payload;
    },
    toggleCombining: (state) => {
      state.portfoliosCombined = !state.portfoliosCombined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getAllPortfolioDashboardAsync.fulfilled, (state, action: PayloadAction<DashboardDetails>) => {
      const multiPortfolios = action.payload.portfolios;
      const clientPortfolioInfoList = multiPortfolios?.clientPortfolioInfoList ?? [];

      const inceptionDates = clientPortfolioInfoList.map((value) => DateTime.fromJSDate(new Date(value.portfolioInceptionDate!)));
      state.portfolioInfo = {
        benchMarkInfoList: multiPortfolios?.benchMarkInfoList ?? [],
        clientPortfolioInfoList: clientPortfolioInfoList,
      };
      state.earliestInception = getEarliestDate(inceptionDates).toISODate() ?? DateTime.now().toISODate();
      state.portfoliosSelected = clientPortfolioInfoList;
      state.portfoliosIdsSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioId || 0);
      state.portfoliosKeysSelected = state.portfoliosSelected.map((portfolio) => portfolio.portfolioKey || '');
      state.adviser = action.payload.adviser;
      state.logoUrl = action.payload.logoUrl;
      state.clientAccounts = action.payload.clients;
      state.portfolioInfoState = ScreenState.SuccessDataLoaded;
    });
    builder.addCase(getAllPortfolioDashboardAsync.rejected, (state) => {
      state.portfolioInfoState = ScreenState.ErrorUnableToLoadData;
    });
    builder.addCase(getAllPortfolioDashboardAsync.pending, (state) => {
      state.portfolioInfoState = ScreenState.Loading;
    });
  },
});

export const { removeSelectedPortfolio, addSelectedPortfolio, toggleCombining, selectOnePortfolioByKey, setCombining } = PortfoliosSlice.actions;

export default PortfoliosSlice.reducer;
